import {hexToRgba} from "./html/html";

interface ISpacing {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
  padding?: number;
  margin?: number;
  horizontal?: number;
  vertical?: number;
}

/**
 * Утилиты для работы со стилями (не прямые CSS свойства)
 */
export class StyleUtils {
  /**
   * Вычисляет размеры width и height
   */
  static size(size: number, options: { unit: string, coefficient: number } = { unit: '%', coefficient: 1 }) {
    if (!size) return { width: '100%', height: '100%' };
    const { unit = '%', coefficient = 1 } = options;
    return { width: `${size * coefficient}${unit}`, height: `${size * coefficient}${unit}` };
  }

  /**
   * Вычисляет минимальные размеры
   */
  static boundaries(boundaries: { minWidth?: number, minHeight?: number }) {
    return {
      minWidth: boundaries?.minWidth ? `${boundaries.minWidth}px` : '100%',
      minHeight: boundaries?.minHeight ? `${boundaries.minHeight}px` : '100%'
    };
  }

  /**
   * Вычисляет отступы в пикселях от процента размера изображения
   */
  static marginsFromImageSize(indents: ISpacing, imageSize: number) {
    // Приоритет: индивидуальные значения > вертикальные/горизонтальные > общий margin
    const marginTop = indents?.top || indents?.vertical || indents?.margin || 0;
    const marginBottom = indents?.bottom || indents?.vertical || indents?.margin || 0;
    const marginLeft = indents?.left || indents?.horizontal || indents?.margin || 0;
    const marginRight = indents?.right || indents?.horizontal || indents?.margin || 0;
    
    // Розраховуємо пікселі від розміру зображення (відсотки вже задані користувачем)
    const marginTopPx = (marginTop / 100) * imageSize;
    const marginBottomPx = (marginBottom / 100) * imageSize;
    const marginLeftPx = (marginLeft / 100) * imageSize;
    const marginRightPx = (marginRight / 100) * imageSize;
    
    return {
      marginTop: `${marginTopPx}px`,
      marginBottom: `${marginBottomPx}px`,
      marginLeft: `${marginLeftPx}px`,
      marginRight: `${marginRightPx}px`
    };
  }

  /**
   * Преобразует цвет с прозрачностью в rgba формат
   */
  static applyColorAlpha(color: string, alpha: number): string {
    if (!color) return `rgba(0, 0, 0, ${alpha})`;
    
    // Если цвет уже в формате rgba, заменяем альфа-канал
    const rgbaMatch = color.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*[\d.]+)?\s*\)/i);
    if (rgbaMatch) {
      return `rgba(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]}, ${alpha})`;
    }
    
    // Используем метод colorToRgba
    return StyleUtils.colorToRgba(color, alpha);
  }

  /**
   * Нормализация цвета к rgba с учётом hex/#rgb/rgb/rgba
   */
  static colorToRgba(input: string, alpha: number): string {
    const trimmed = (input || '').trim();
    if (trimmed.startsWith('rgba(')) return trimmed;
    if (trimmed.startsWith('rgb(')) {
      const inside = trimmed.slice(4, -1);
      return `rgba(${inside}, ${alpha})`;
    }
    if (trimmed.startsWith('#')) {
      const hex = trimmed.length === 4
        ? `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`
        : trimmed;
      return hexToRgba(hex, alpha);
    }
    return `rgba(0, 0, 0, ${alpha})`;
  }

  /**
   * Генерирует строку box-shadow из цвета с настраиваемыми параметрами
   */
  static boxShadow(color: string, options?: { alpha?: number; blur?: number; offsetX?: number; offsetY?: number }) {
    const { alpha = 0.22, blur = 10, offsetX = 0, offsetY = 0 } = options || {};
    const rgba = StyleUtils.colorToRgba(color, alpha);
    return `${offsetX}px ${offsetY}px ${blur}px ${rgba}`;
  }

  /**
   * Проверяет, является ли цвет черным по умолчанию
   */
  static isDefaultBlackColor(color: string): boolean {
    const normalized = (color ?? '').toString().trim().toLowerCase();
    return (
      normalized === '0 0 0' ||
      normalized === '0,0,0' ||
      normalized === 'black' ||
      normalized === '#000' ||
      normalized === '#000000' ||
      normalized === '#000000ff' ||
      normalized === 'rgb(0 0 0)' ||
      normalized === 'rgb(0,0,0)' ||
      normalized === 'rgba(0 0 0 / 1)' ||
      normalized === 'rgba(0,0,0,1)'
    );
  }

  /**
   * Обрабатывает цветовую остановку градиента (применяет прозрачность и позицию)
   */
  static processColorStop(
    colorStop: string | { color: string; position?: number; alpha?: number },
    positionUnit: 'px' | '%' | 'deg' = '%'
  ): string {
    if (typeof colorStop === 'string') {
      return colorStop;
    }
    
    let finalColor = colorStop.color;
    // Применяем прозрачность, если указана
    if (colorStop.alpha !== undefined && colorStop.alpha !== null) {
      finalColor = StyleUtils.applyColorAlpha(colorStop.color, colorStop.alpha);
    }
    
    // Добавляем позицию, если указана
    if (colorStop.position !== undefined) {
      const unit = positionUnit === 'deg' ? 'deg' : positionUnit;
      return `${finalColor} ${colorStop.position}${unit}`;
    }
    
    return finalColor;
  }

  /**
   * Создает строку линейного градиента
   */
  static createLinearGradient(config: {
    colors: Array<string | { color: string; position?: number; alpha?: number }>;
    angle?: number;
    direction?: 'to top' | 'to bottom' | 'to left' | 'to right' | 'to top right' | 'to top left' | 'to bottom right' | 'to bottom left';
  }): string {
    const { colors, angle, direction } = config;
    
    // Формируем начало строки градиента
    let gradientStr = 'linear-gradient(';
    
    if (angle !== undefined) {
      gradientStr += `${angle}deg`;
    } else if (direction) {
      gradientStr += direction;
    } else {
      gradientStr += '180deg'; // По умолчанию сверху вниз
    }
    
    // Обрабатываем цвета
    const colorStops = colors.map(colorStop => 
      StyleUtils.processColorStop(colorStop, '%')
    );
    
    gradientStr += ', ' + colorStops.join(', ') + ')';
    return gradientStr;
  }

  /**
   * Создает строку радиального градиента
   */
  static createRadialGradient(config: {
    colors: Array<string | { color: string; position?: number; alpha?: number }>;
    shape?: 'circle' | 'ellipse';
    size?: 'closest-side' | 'closest-corner' | 'farthest-side' | 'farthest-corner' | string;
    center?: { x?: string | number; y?: string | number };
    position?: string;
  }): string {
    const { colors, shape = 'ellipse', size = 'farthest-corner', center = {}, position } = config;
    
    const positionValue = position || 
      (center.x !== undefined && center.y !== undefined 
        ? `${center.x}${typeof center.x === 'number' ? '%' : ''} ${center.y}${typeof center.y === 'number' ? '%' : ''}`
        : 'center');
    
    const colorStops = colors.map(colorStop => 
      StyleUtils.processColorStop(colorStop, '%')
    );
    
    return `radial-gradient(${shape} ${size} at ${positionValue}, ${colorStops.join(', ')})`;
  }

  /**
   * Создает строку конического градиента
   */
  static createConicGradient(config: {
    colors: Array<string | { color: string; position?: number; alpha?: number }>;
    angle?: number;
    center?: { x?: string | number; y?: string | number };
    position?: string;
  }): string {
    const { colors, angle = 0, center = {}, position } = config;
    
    const positionValue = position || 
      (center.x !== undefined && center.y !== undefined 
        ? `${center.x}${typeof center.x === 'number' ? '%' : ''} ${center.y}${typeof center.y === 'number' ? '%' : ''}`
        : 'center');
    
    const colorStops = colors.map(colorStop => 
      StyleUtils.processColorStop(colorStop, 'deg')
    );
    
    return `conic-gradient(from ${angle}deg at ${positionValue}, ${colorStops.join(', ')})`;
  }

  /**
   * Создает градиент из одного цвета (для совместимости)
   */
  static gradientColor(color: string, isShown: boolean = true) {
    if (!isShown) return {};
    
    // Создаем простой градиент напрямую
    return {
      background: `linear-gradient(90deg, ${color}, ${color})`
    };
  }

  /**
   * Комбинирует фон и паттерн
   */
  static backgroundWithPattern(backgroundColor: string, isBackgroundShown: boolean, patternData: any, isPatternShown: boolean = true) {
    if (!isBackgroundShown && !isPatternShown) return {};
    
    let backgroundValue = '';
    
    // Додаємо фоновий колір
    if (isBackgroundShown && backgroundColor) {
      if (!StyleUtils.isDefaultBlackColor(backgroundColor)) {
        backgroundValue = backgroundColor;
      }
    }
    
    // Додаємо патерн
    if (isPatternShown && patternData?.url) {
      const opacity = parseFloat(patternData?.opacity) || 1;
      const scale = parseFloat(patternData?.scale) || 1;
      const imageWidth = patternData?.width || 100;
      const imageHeight = patternData?.height || 100;
      const scaledWidth = imageWidth * scale;
      const scaledHeight = imageHeight * scale;
      
      const patternValue = `url(${patternData.url})`;
      const patternSize = `${scaledWidth}px ${scaledHeight}px`;
      
      if (backgroundValue) {
        backgroundValue = `${patternValue} ${backgroundValue}`;
      } else {
        backgroundValue = patternValue;
      }
      
      return {
        background: backgroundValue,
        backgroundRepeat: 'repeat',
        backgroundSize: patternSize,
        backgroundPosition: '0% 0%',
        backgroundAttachment: 'local',
        backgroundOrigin: 'border-box',
        backgroundClip: 'border-box'
      };
    }
    
    return {
      background: backgroundValue || 'transparent'
    };
  }

  /**
   * Создает CSS переменные для паттерна через псевдоелемент
   */
  static pattern(patternData: any, isShown: boolean = true) {
    if (!isShown || !patternData?.url) {
      return {};
    }

    // Переконуємося що opacity та scale є числами
    const opacity = parseFloat(patternData?.opacity) || 1;
    const scale = parseFloat(patternData?.scale) || 1;
    
    // Отримуємо розміри зображення
    const imageWidth = patternData?.width || 100; // За замовчуванням 100px
    const imageHeight = patternData?.height || 100; // За замовчуванням 100px
    
    // Застосовуємо масштаб до розмірів зображення
    const scaledWidth = imageWidth * scale;
    const scaledHeight = imageHeight * scale;

    return {
      '--pattern-url': `url(${patternData.url})`,
      '--pattern-width': `${scaledWidth}px`,
      '--pattern-height': `${scaledHeight}px`,
      '--pattern-opacity': opacity
    };
  }
}

